import { Box, Button, Checkbox, FormGroup, Modal } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IGroupExportColumns, IOrganizationExportColumns, IPractitionerExportColumns } from '@common/interfaces/';
import type { IGroupFilters } from '@common/validation/group';
import type { IOrganizationFilters } from '@common/validation/organization';
import type { IPractitionerFilters } from '@common/validation/practitioner';
import {
    ActiveOrInactive,
    GroupExportColumns,
    ManagerAuthorization,
    OrganizationsExportColumns,
    PractitionersExportColumns,
} from '@common/enums/';
import { KgbExportType } from '@common/enums/kgbExportType';
import { never } from '@common/utils/never';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import { Loader } from './common';

type KgbExportModalProps = {
    areAllChecked: boolean;
    deselectItems: () => void;
    exportType: KgbExportType;
    filters?: IPractitionerFilters | IOrganizationFilters | IGroupFilters;
    isOpen: boolean;
    onClose: () => void;
    selectedItems?: string[];
    title: string;
};

type KgbExportColumns = IOrganizationExportColumns | IPractitionerExportColumns | IGroupExportColumns;

export function KgbExportModal(props: Readonly<KgbExportModalProps>) {
    const abortableFetch = useAbortableFetch();
    const [exportColumns, setExportColumns] = useState<KgbExportColumns>();
    const [exportActiveCarePaths, setExportActiveCarePaths] = useState<boolean>(false);
    const [exportInactiveCarePaths, setExportInactiveCarePaths] = useState<boolean>(false);
    const [canExport, setCanExport] = useState<boolean>();
    const [isExporting, setIsExporting] = useState(false);
    const { exportType, title, isOpen, onClose, selectedItems, areAllChecked, filters, deselectItems } = props;

    const translate = getSelectedLanguage();

    const groupExportColumns = [
        { key: GroupExportColumns.name, text: translate.groups.nameGroup },
        { key: GroupExportColumns.status, text: translate.jobs.status },
        { key: GroupExportColumns.identifierDeclaration, text: translate.organization.agbCode },
        { key: GroupExportColumns.relationNumber, text: translate.organization.relationNumber },
        { key: GroupExportColumns.financialEmailAddress, text: translate.groups.financialEmailAddress },
        { key: GroupExportColumns.organizations, text: translate.organization.organizations },
    ];

    const organizationExportColumns = [
        { key: OrganizationsExportColumns.name, text: translate.organization.nameOrganization },
        { key: OrganizationsExportColumns.previousNames, text: translate.organization.previousNames },
        { key: OrganizationsExportColumns.nameStatutory, text: translate.organization.nameStatutory },
        { key: OrganizationsExportColumns.alias, text: translate.organization.alias },
        { key: OrganizationsExportColumns.kvkNumber, text: translate.organization.kvkNumber },
        { key: OrganizationsExportColumns.txcsId, text: translate.common.Id },
        { key: OrganizationsExportColumns.organizationType, text: translate.organization.organizationType },
        { key: OrganizationsExportColumns.description, text: translate.organization.notes },
        { key: OrganizationsExportColumns.status, text: translate.jobs.status },
        {
            key: OrganizationsExportColumns.activeAgbCode,
            text: translate.organization.agbCode,
        },
        { key: OrganizationsExportColumns.emailAddressRequestInbox, text: translate.common.emailAddress },
        { key: OrganizationsExportColumns.phoneNumber, text: translate.common.phoneNumber },
        {
            key: OrganizationsExportColumns.cityAndZip,
            text: `${translate.common.city} ${translate.common.and} ${translate.common.zipcode.toLowerCase()}`,
        },
        { key: OrganizationsExportColumns.state, text: translate.common.province },
        { key: OrganizationsExportColumns.relationNumber, text: translate.organization.relationNumber },
        { key: OrganizationsExportColumns.emailAddressPayouts, text: translate.employment.financialEmailAddress },
        { key: OrganizationsExportColumns.activeCarepaths, text: translate.organization.activeCarepaths },
        { key: OrganizationsExportColumns.activeGroups, text: translate.common.careGroup },
        { key: OrganizationsExportColumns.startDate, text: translate.common.startingDate },
        { key: OrganizationsExportColumns.endDate, text: translate.common.endDate },
        {
            key: OrganizationsExportColumns.canPerformGlaucoomExamination,
            text: translate.serviceOrganizationSetting.canPerformGlaucoomExamination,
        },
        ...(hasOneAuthorization(ManagerAuthorization.organizationFinanceData)
            ? [
                  {
                      key: OrganizationsExportColumns.bankAccountSubscriber,
                      text: translate.organization.subscriber,
                  },
                  {
                      key: OrganizationsExportColumns.bankAccountNumber,
                      text: translate.organization.bankAccountNumber,
                  },
                  {
                      key: OrganizationsExportColumns.bankAccountStatus,
                      text: translate.organization.bankAccountStatus,
                  },
              ]
            : []),
    ];
    const practitionerExportColumns = [
        { key: PractitionersExportColumns.name, text: translate.practitioner.namePractitioner },
        { key: PractitionersExportColumns.id, text: translate.common.Id },
        {
            key: PractitionersExportColumns.identifierDeclaration,
            text: translate.identifierDeclarationOrRegistration.identifierDeclaration,
        },
        {
            key: PractitionersExportColumns.identifierRegistrationBig,
            text: translate.practitioner.identifierRegistrationBig,
        },
        {
            key: PractitionersExportColumns.identifierRegistrationKp,
            text: translate.practitioner.identifierRegistrationKp,
        },
        { key: PractitionersExportColumns.emailAddress, text: translate.common.emailAddress },
        { key: PractitionersExportColumns.mobileNumber, text: translate.common.mobileNumber },
        { key: PractitionersExportColumns.organizations, text: translate.organization.organizations },
        { key: PractitionersExportColumns.zorgmailNummer, text: translate.employment.zorgmailNummer },
        { key: PractitionersExportColumns.employments, text: translate.common.profession },
        { key: PractitionersExportColumns.relationNumber, text: translate.employment.relationNumber },
        {
            key: PractitionersExportColumns.financialEmailAddress,
            text: translate.employment.financialEmailAddress,
        },
        { key: PractitionersExportColumns.service, text: translate.service.services },
        { key: PractitionersExportColumns.lastActivityDate, text: translate.practitioner.lastActivityDate },
        { key: PractitionersExportColumns.isActivated, text: translate.profile.accountActivated },
        ...(hasOneAuthorization(ManagerAuthorization.employmentFinanceData)
            ? [
                  {
                      key: PractitionersExportColumns.bankAccountSubscriber,
                      text: translate.employment.subscriber,
                  },
                  {
                      key: PractitionersExportColumns.bankAccountNumber,
                      text: translate.employment.bankAccountNumber,
                  },
                  {
                      key: PractitionersExportColumns.bankAccountStatus,
                      text: translate.employment.bankAccountStatus,
                  },
              ]
            : []),
    ];

    useEffect(() => {
        if (exportType === KgbExportType.organization) {
            setExportColumns({
                activeAgbCode: true,
                activeCarepaths: false,
                activeGroups: false,
                bankAccountNumber: false,
                bankAccountStatus: false,
                bankAccountSubscriber: false,
                canPerformGlaucoomExamination: false,
                cityAndZip: true,
                description: false,
                emailAddressPayouts: false,
                emailAddressRequestInbox: true,
                endDate: false,
                kvkNumber: true,
                name: true,
                nameStatutory: true,
                organizationType: true,
                phoneNumber: false,
                previousNames: false,
                relationNumber: false,
                startDate: false,
                state: true,
                status: false,
                txcsId: true,
            });
        } else if (exportType === KgbExportType.group) {
            setExportColumns({
                financialEmailAddress: false,
                identifierDeclaration: false,
                name: true,
                organizations: false,
                relationNumber: true,
                status: true,
                useHealthConnected: false,
            });
        } else {
            setExportColumns({
                bankAccountNumber: false,
                bankAccountStatus: false,
                bankAccountSubscriber: false,
                emailAddress: true,
                employments: true,
                financialEmailAddress: false,
                id: true,
                identifierDeclaration: true,
                identifierRegistrationBig: false,
                identifierRegistrationKp: false,
                isActivated: false,
                lastActivityDate: false,
                mobileNumber: false,
                name: true,
                organizations: true,
                relationNumber: false,
                service: false,
                zorgmailNummer: false,
            });
        }
    }, []);

    useEffect(() => {
        if (
            exportColumns &&
            (exportColumns as IPractitionerExportColumns).service &&
            !exportActiveCarePaths &&
            !exportInactiveCarePaths
        ) {
            setExportColumns({ ...exportColumns, service: false });
        }
    }, [exportActiveCarePaths, exportInactiveCarePaths]);

    useEffect(() => {
        const isAnyColumnSelected = exportColumns && Object.values(exportColumns).some((value) => value);
        setCanExport(isAnyColumnSelected);
    }, [exportColumns]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="medium"
        >
            <Modal.Header>{title}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                {isExporting && (
                    <Loader
                        centered={true}
                        large={true}
                    />
                )}
                {renderExportColumns()}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={onClose}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
                <Button
                    isDisabled={!canExport || isExporting}
                    onClick={async () => await downloadExport()}
                >
                    {translate.common.export}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function renderExportColumns() {
        let exportColumnsNames: { key: string; text: string }[];
        switch (exportType) {
            case KgbExportType.organization:
                exportColumnsNames = organizationExportColumns;
                break;
            case KgbExportType.practitioner:
                exportColumnsNames = practitionerExportColumns;
                break;
            case KgbExportType.group:
                exportColumnsNames = groupExportColumns;
                break;
            default:
                never(exportType);
        }

        return (
            <>
                {exportColumns && (
                    <FormGroup label={translate.common.exportData}>
                        {exportColumnsNames.map((column) => {
                            return (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={2}
                                    key={column.key}
                                    pb={2}
                                    sx={{ mt: [0, null, 1] }}
                                >
                                    <Checkbox
                                        checked={exportColumns[column.key as keyof KgbExportColumns]}
                                        disabled={isExporting}
                                        key={column.key}
                                        label={column.text}
                                        onChange={(e) => {
                                            setExportColumns({
                                                ...exportColumns,
                                                [column.key as keyof KgbExportColumns]: e.target.checked,
                                            });

                                            if (column.key === PractitionersExportColumns.service) {
                                                setExportActiveCarePaths(e.target.checked);
                                                setExportInactiveCarePaths(e.target.checked);
                                            }
                                        }}
                                    />
                                    {column.key === PractitionersExportColumns.service &&
                                        (exportColumns as IPractitionerExportColumns).service && (
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                gap={2}
                                                pl={8}
                                            >
                                                <Checkbox
                                                    checked={exportActiveCarePaths}
                                                    disabled={isExporting}
                                                    label="Actieve rechten"
                                                    onChange={(e) => {
                                                        setExportActiveCarePaths(e.target.checked);
                                                    }}
                                                />
                                                <Checkbox
                                                    checked={exportInactiveCarePaths}
                                                    disabled={isExporting}
                                                    label="Inactieve rechten"
                                                    onChange={(e) => {
                                                        setExportInactiveCarePaths(e.target.checked);
                                                    }}
                                                />
                                            </Box>
                                        )}
                                </Box>
                            );
                        })}
                    </FormGroup>
                )}
            </>
        );
    }

    async function downloadExport() {
        setIsExporting(true);

        if (exportType === KgbExportType.practitioner) {
            const result = await abortableFetch.fetchJson('/api/practitioners/exportList', 'POST', {
                exportAll: areAllChecked,
                filters: filters,
                selectedColumns: exportColumns,
                selectedItems: selectedItems,
                serviceStatus: getServiceStatus(),
            });

            window.open(`/api/exportDownloads/${result.json.exportDownloadId}/practitioners.xlsx`);
        }
        if (exportType === KgbExportType.organization) {
            const result = await abortableFetch.fetchJson('/api/organizations/export', 'POST', {
                exportAll: areAllChecked,
                filters: filters,
                selectedColumns: exportColumns,
                selectedItems: selectedItems,
            });
            window.open(`/api/exportDownloads/${result.json.exportDownloadId}/organizations.xlsx`);
        }

        if (exportType === KgbExportType.group) {
            const result = await abortableFetch.fetchJson('/api/groups/export', 'POST', {
                filters: filters,
                selectedColumns: exportColumns,
                selectedItems: selectedItems,
            });
            window.open(`/api/exportDownloads/${result.json.exportDownloadId}/groups.xlsx`);
        }

        setIsExporting(false);
        deselectItems();
        onClose();
    }

    function getServiceStatus() {
        if (exportActiveCarePaths && !exportInactiveCarePaths) {
            return ActiveOrInactive.active;
        } else if (!exportActiveCarePaths && exportInactiveCarePaths) {
            return ActiveOrInactive.inactive;
        }
    }
}
